<template>
  <BaseFormLayout
    :title="
      editMode ? `Edit ${fleetModule.driver}` : `Add ${fleetModule.driver}`
    "
    :nonFieldErrors="formErrors.non_field_errors"
  >
    <template #actions>
      <SecondaryButton
        v-if="!editMode"
        id="driversReset"
        @click="resetForm()"
        btnClass="mr-2 text-uppercase rounded-lg"
        name="Reset"
      >
      </SecondaryButton>
      <PrimaryButton
        id="driversSubmit"
        @click="submitForm()"
        :disabled="!isValid"
        name="Submit"
        btnClass="text-uppercase rounded-lg"
      >
      </PrimaryButton>
    </template>
    <template #content>
      <v-form
        v-model="isValid"
        ref="driverForm"
        :key="formCount"
        @submit.prevent="submitForm"
      >
        <v-row no-gutters>
          <v-col cols="12" lg="8" class="pa-0 pr-lg-2">
            <v-card elevation="0" class="rounded-lg pa-4 h-100">
              <v-row>
                <v-col
                  cols="12"
                  lg="5"
                  class="d-flex justify-center align-center"
                >
                  <ProfileUpload
                    v-model="profileImage"
                    @UploadProfile="updateProfile"
                  />
                </v-col>
                <v-col cols="12" lg="7">
                  <v-row class="ma-0">
                    <v-col cols="12">
                      <span class="text-subtitle-1 font-weight-bold"
                        >User Information</span
                      >
                    </v-col>
                    <v-col cols="12">
                      <InputField
                        id="username"
                        v-model="driverDetails.username"
                        label="Username"
                        :required="true"
                        name="username"
                        :rules="[
                          (val) =>
                            (val && usernameValidation(val)) ||
                            'No special characters are allowed',
                        ]"
                        :error-messages="formErrors.username"
                        @input="delete formErrors.username"
                      />
                    </v-col>
                    <v-col cols="6">
                      <InputField
                        id="first_name"
                        v-model="driverDetails.first_name"
                        label="First Name"
                        :required="true"
                        name="first_name"
                        :rules="[
                          (val) => {
                            return (
                              !(/[0-9]/gi.test(val) && !/[a-z]/gi.test(val)) ||
                              'Can not be just numbers!'
                            );
                          },
                          (v) =>
                            !/[^a-zA-Z0-9 _.-]/.test(v) ||
                            'Special characters are not allowed',
                        ]"
                        :error-messages="formErrors.first_name"
                        @input="delete formErrors.first_name"
                      />
                    </v-col>
                    <v-col cols="6">
                      <InputField
                        id="last_name"
                        v-model="driverDetails.last_name"
                        label="Last Name"
                        :required="true"
                        name="last_name"
                        :rules="[
                          (val) => {
                            return (
                              !(/[0-9]/gi.test(val) && !/[a-z]/gi.test(val)) ||
                              'Can not be just numbers!'
                            );
                          },
                          (v) =>
                            !/[^a-zA-Z0-9 _.-]/.test(v) ||
                            'Special characters are not allowed',
                        ]"
                        :error-messages="formErrors.last_name"
                        @input="delete formErrors.last_name"
                      />
                    </v-col>
                    <v-col cols="12">
                      <InputField
                        id="contact_number"
                        type="number"
                        v-model="driverDetails.contact_number"
                        label="Contact Number"
                        name="contact_number"
                        :required="true"
                        :rules="[
                          (val) =>
                            (val && val.length >= 9 && val.length <= 12) ||
                            'Contact number should be between 9 to 12 digits!',
                        ]"
                        :error-messages="formErrors.contact_number"
                        @input="delete formErrors.contact_number"
                      />
                    </v-col>

                    <v-col cols="6">
                      <PasswordField
                        id="password"
                        label="Password"
                        name="password"
                        v-model="driverDetails.password"
                        :required="!editMode"
                        :error-messages="formErrors.password"
                        :rules="
                          driverDetails.password != null &&
                          driverDetails.confirm_password != null
                            ? passwordValidation
                            : []
                        "
                        @input="delete formErrors.password"
                      />
                    </v-col>
                    <v-col cols="6">
                      <PasswordField
                        id="confirm_password"
                        label="Confirm password"
                        name="confirm_password"
                        v-model="driverDetails.confirm_password"
                        :required="!editMode || !!driverDetails.password"
                        :rules="
                          driverDetails.password != null &&
                          driverDetails.confirm_password != null
                            ? passwordValidation
                            : []
                        "
                        :error-messages="formErrors.confirm_password"
                        @input="delete formErrors.confirm_password"
                      />
                    </v-col>

                    <v-col cols="12">
                      <SelectField
                        id="license_type"
                        v-model="driverDetails.license_type"
                        :itemsList="licenseTypeList"
                        name="license_type"
                        label="License Type"
                        :error-messages="formErrors.license_type"
                        @input="delete formErrors.license_type"
                      />
                    </v-col>

                    <v-col cols="12" md="4" lg="4" class="d-flex align-center">
                      <SwitchField
                        id="is_active"
                        v-model="driverDetails.is_active"
                        name="is_active"
                        inset
                        hide-details
                        :label="`${
                          driverDetails.is_active ? 'Active' : 'Deactivated'
                        }`"
                        :error-messages="formErrors.is_active"
                        @input="delete formErrors.is_active"
                      ></SwitchField>
                    </v-col>
                    <v-col
                      cols="12"
                      md="4"
                      lg="8"
                      v-if="!driverDetails.is_active"
                    >
                      <InputField
                        id="deactivation_remarks"
                        v-model="driverDetails.deactivation_remarks"
                        :required="true"
                        label="Deactivation Remarks"
                        name="deactivation_remarks"
                        :error-messages="formErrors.deactivation_remarks"
                        @input="delete formErrors.deactivation_remarks"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
          <v-col cols="12" lg="4" class="px-0 pb-0 pt-4 pl-lg-2 pt-lg-0">
            <v-card elevation="0" class="pa-4">
              <v-row class="ma-0">
                <v-col cols="12">
                  <span class="text-subtitle-1 font-weight-bold">
                    Basic Information
                  </span>
                </v-col>

                <v-col cols="12">
                  <SelectField
                    id="branch"
                    v-model="driverDetails.branch"
                    :label="`${configurationModule.branch}*`"
                    item-text="name"
                    item-value="id"
                    :items-list="branchList"
                    name="branch"
                    :rules="[
                      (val) =>
                        !!val || `${configurationModule.branch} is required`,
                    ]"
                    :error-messages="formErrors.branch"
                    @change="
                      delete formErrors.branch;
                      getVehicleList($event);
                      getProjectSelectList($event);
                      getZoneList($event);
                      setAutoCompletedRestriction();
                    "
                  />
                </v-col>

                <v-col cols="12">
                  <SelectField
                    id="project"
                    v-model="driverDetails.project"
                    :itemsList="projectList"
                    itemText="name"
                    itemValue="id"
                    name="project"
                    label="Project*"
                    :required="true"
                    :rules="[
                      (val) =>
                        !!val || `${configurationModule.project} is required`,
                    ]"
                    :error-messages="formErrors.project"
                    @input="delete formErrors.project"
                  />
                </v-col>

                <v-col cols="12">
                  <SelectField
                    id="zones"
                    v-model="driverDetails.zones"
                    :itemsList="zoneList"
                    :multiple="true"
                    name="zones"
                    itemText="name"
                    itemValue="id"
                    :label="tripPlanningModule.driverZone"
                    :error-messages="formErrors.zones"
                    @input="delete formErrors.zones"
                  />
                </v-col>
              </v-row>
            </v-card>
            <v-card elevation="0" class="pa-4 mt-4">
              <v-row class="ma-0">
                <v-col cols="12">
                  <span class="text-subtitle-1 font-weight-bold">
                    Address Information
                  </span>
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    outlined
                    v-model="driverDetails.address"
                    hide-details="auto"
                    background-color="background_color"
                    label="Address"
                    dense
                    id="addressField"
                    name="address"
                    ref="addressField"
                    clearable
                    :error-messages="formErrors.address"
                    @input="
                      delete formErrors.address;
                      delete formErrors.coordinates;
                    "
                    @click:clear="setRestLocation"
                  />
                </v-col>

                <v-col cols="6">
                  <InputField
                    id="latitude"
                    v-if="driverDetails && driverDetails.coordinates"
                    disabled
                    class="unassigned lighten-3"
                    name="latitude"
                    v-model="driverDetails.coordinates.latitude"
                    label="Latitude"
                    :error-messages="formErrors.coordinates"
                    @input="delete formErrors.coordinates"
                  />
                </v-col>
                <v-col cols="6">
                  <InputField
                    id="longitude"
                    v-if="driverDetails && driverDetails.coordinates"
                    disabled
                    class="unassigned lighten-3"
                    name="longitude"
                    v-model="driverDetails.coordinates.longitude"
                    label="Longitude"
                    :error-messages="formErrors.coordinates"
                    @input="delete formErrors.coordinates"
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <v-row no-gutters class="mt-4">
          <v-col cols="12" lg="4" class="pa-0 pr-lg-4">
            <v-card elevation="0" class="rounded-lg pa-4 h-100">
              <v-row class="ma-0">
                <v-col cols="6" class="pb-0">
                  <span
                    class="font-weight-bold text-subtitle-1 text-capitalize"
                  >
                    {{ `${fleetModule.vehicle} Information` }}
                  </span>
                </v-col>
                <v-col cols="6" class="text-right">
                  <v-chip
                    label
                    color="primary"
                    text-color="white"
                    class="text-caption"
                    v-show="driverDetails && driverDetails.vehicle_number"
                  >
                    Assigned Vehicle
                    <span class="mx-2">:</span>
                    <b> {{ driverDetails.vehicle_number }}</b>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          small
                          class="ml-2"
                          v-bind="attrs"
                          v-on="on"
                          @click="deleteVehicle"
                        >
                          mdi-close
                        </v-icon>
                      </template>
                      <span class="text-capitalize"
                        >unassign {{ `${fleetModule.vehicle}` }}</span
                      >
                    </v-tooltip>
                  </v-chip>
                </v-col>
                <v-col cols="12">
                  <SelectField
                    id="vehicle_assigned"
                    name="vehicle_assigned"
                    :label="`${
                      !editMode
                        ? `${fleetModule.vehicle} Assigned`
                        : `change ${fleetModule.vehicle}`
                    }`"
                    :itemsList="vehicleList"
                    item-text="vehicle_number"
                    item-value="id"
                    v-model="driverDetails.vehicle_assigned"
                    :error-messages="formErrors.vehicle_assigned"
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>
          <v-col cols="12" lg="8" class="mt-4 mt-lg-0">
            <v-card elevation="0" class="rounded-lg pa-4 h-100">
              <v-row class="ma-0">
                <v-col cols="12">
                  <span class="text-subtitle-1 font-weight-bold"
                    >Shift Information</span
                  >
                </v-col>
                <v-col cols="12" md="4" lg="3">
                  <InputField
                    id="shift_start"
                    type="time"
                    v-model="driverDetails.shift_start"
                    label="Shift Start"
                    name="shift_start"
                    :required="true"
                    :error-messages="formErrors.shift_start"
                    @input="delete formErrors.shift_start"
                  />
                </v-col>
                <v-col cols="12" md="4" lg="3">
                  <InputField
                    id="shift_end"
                    type="time"
                    v-model="driverDetails.shift_end"
                    label="Shift End"
                    name="shift_end"
                    :required="true"
                    :error-messages="formErrors.shift_end"
                    @input="delete formErrors.shift_end"
                  />
                </v-col>
                <v-col cols="12" md="4" lg="3">
                  <InputField
                    id="break_start"
                    type="time"
                    v-model="driverDetails.break_start"
                    label="Break Start"
                    name="break_start"
                    :error-messages="formErrors.break_start"
                    @input="delete formErrors.break_start"
                  />
                </v-col>
                <v-col cols="12" md="4" lg="3">
                  <InputField
                    id="break_end"
                    type="time"
                    v-model="driverDetails.break_end"
                    label="Break End"
                    name="break_end"
                    :error-messages="formErrors.break_end"
                    @input="delete formErrors.break_end"
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
        <v-row
          class="mt-4 mt-lg-0"
          v-if="customOptionFieldList && customOptionFieldList.length > 0"
        >
          <v-col cols="12">
            <CustomFieldRenderForm
              :fieldList="customOptionFieldList"
              :formObject="customObject"
              :fieldsErrors="customFieldsErrors"
            />
          </v-col>
        </v-row>
      </v-form>
    </template>
  </BaseFormLayout>
</template>

<script>
import { bus } from "@/main.js";
import BaseFormLayout from "@/components/LayoutComponents/BaseFormLayout.vue";
import {
  generatedFormData,
  loadGoogleInstance,
  usernameValidation,
  customFieldDefaultData,
} from "@/utils/functions";
import {
  configurationModule,
  fleetModule,
  tripPlanningModule,
} from "@/utils/moduleMapping";

export default {
  components: {
    BaseFormLayout,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    editMode: {
      required: true,
    },
  },
  data() {
    return {
      customFieldDefaultData,
      usernameValidation,
      tripPlanningModule,
      configurationModule,
      fleetModule,
      viewPassword: false,
      oldPassword: false,

      fileInput: null,
      formCount: 0,
      isValid: true,
      driverDetails: {
        coordinates: {},
        is_active: true,
        zones: [],
      },
      formErrors: {},

      branchList: [],
      vehicleList: [],

      zoneList: [],
      licenseTypeList: [],
      projectList: [],

      profileImage: null,
      userDetails: this.$helper.getUserDetailsLocalStorage(),
      customOptionFieldList: [],
      customFieldsErrors: {},
      customObject: {},
    };
  },
  computed: {
    passwordValidation() {
      return [
        this.driverDetails.password == this.driverDetails.confirm_password ||
          `Password does not match`,
      ];
    },
    selectedBranch() {
      let obj = this.$helper.getUserDetailsLocalStorage();
      if (obj) {
        return obj.preferred_branch.id;
      } else {
        return null;
      }
    },
  },
  methods: {
    updateProfile(image, isChangeable = false) {
      if (this.driverDetails.update_profile_image || isChangeable) {
        this.driverDetails.update_profile_image = true;
        if (image) {
          this.driverDetails.profile_image = image;
        } else {
          delete this.driverDetails.profile_image;
        }
      } else {
        this.driverDetails.update_profile_image = false;
        delete this.driverDetails.profile_image;
      }
    },
    resetForm() {
      this.$refs.driverForm.resetValidation();
      this.driverDetails = {
        coordinates: {},
        is_active: true,
        zones: [],
        branch: this.selectedBranch,
      };
      this.zoneList = [];
      this.vehicleList = [];
      this.getDriverOptions();
    },
    getDriverObject(id) {
      bus.$emit("showLoader", true);
      this.$api.driver
        .getDriverObject(id)
        .then((res) => {
          this.driverDetails = res.data;

          if (
            res &&
            res.data &&
            res.data.custom_fields &&
            Object.keys(res.data.custom_fields).length > 0
          ) {
            this.customObject = res?.data?.custom_fields;
          }

          if (res && res.data && res.data.profile_image) {
            this.profileImage = res.data.profile_image;
            delete res.data.profile_image;
          }
          this.getVehicleList(this.driverDetails.branch);
          this.getZoneList(this.driverDetails.branch);
          this.getProjectSelectList(this.driverDetails.branch);
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    deleteVehicle() {
      this.$root
        .$confirm("Delete?", "Are you sure you want to unassign vehicle?", {
          color: "error",
        })
        .then(async (confirm) => {
          if (confirm) {
            let obj = { ...this.driverDetails };
            delete obj.vehicle_number;
            delete obj.vehicle_assigned;
            this.driverDetails = obj;
          }
        });
    },
    submitForm() {
      if (!this.$refs.driverForm.validate()) {
        bus.$emit("showToastMessage", {
          message: "Please correct all the errors to submit the form!",
          color: "error",
        });
        return;
      }
      let data = {
        ...this.driverDetails,
        custom_fields: JSON.stringify(this.customObject),
      };
      bus.$emit("showLoader", true);
      if (this.editMode) {
        if (data && !data.update_profile_image) {
          delete data.profile_image;
        }
        if (data && !data.vehicle_assigned) {
          delete data.vehicle_assigned;
        }
        this.$api.driver
          .updateDriverObject({
            id: this.driverDetails.id,
            data: generatedFormData(data),
          })
          .then(() => {
            bus.$emit("showLoader", false);
            bus.$emit("showToastMessage", {
              message: "Driver information updated!",
              color: "success",
            });
            this.resetForm();
            this.$router.back();
          })
          .catch((err) => {
            bus.$emit("showLoader", false);
            this.formErrors = err.data;
            this.customFieldsErrors = err?.data?.custom_fields;
          });
      } else {
        this.$api.driver
          .createDriverObject(generatedFormData(data))
          .then(() => {
            bus.$emit("showLoader", false);
            bus.$emit("showToastMessage", {
              message: "Driver created!",
              color: "success",
            });
            this.resetForm();
            this.$router.back();
          })
          .catch((err) => {
            bus.$emit("showLoader", false);
            this.formErrors = err.data;
            this.customFieldsErrors = err?.data?.custom_fields;
          });
      }
    },
    async getBranchList() {
      bus.$emit("showLoader", true);
      await this.$api.branch
        .getBranchSelectList({ limit: "all" })
        .then((res) => {
          this.branchList = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    getZoneList(id) {
      bus.$emit("showLoader", true);
      this.$api.driverZone
        .getDriverZoneList({ limit: "all", branch: id })
        .then((res) => {
          this.zoneList = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    getVehicleList(id) {
      bus.$emit("showLoader", true);
      this.$api.vehicle
        .getVehicleList({ limit: "all", branch: id, has_assigned: false })
        .then((res) => {
          this.vehicleList = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    getFieldConfigurationList() {
      bus.$emit("showLoader", true);
      this.$api.fieldConfiguration
        .getFieldConfigurationList({ limit: "all", search: "license type" })
        .then((res) => {
          this.licenseTypeList = res.data.map((field) => field.name);
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    setAutoCompletedRestriction() {
      if (this.driverDetails.branch && this.branchList) {
        let branch = this.branchList.find(
          (v) => v.id == this.driverDetails.branch
        );
        if (branch && branch.country_code) {
          this.addressField.setComponentRestrictions({
            country: [branch.country_code],
          });
        }
      }
      this.setRestLocation();
    },
    setRestLocation() {
      this.driverDetails.address = null;
      this.driverDetails.coordinates = {};
    },
    loadAutoComplete() {
      const interval = setInterval(async () => {
        if (this.$refs.addressField && window.google && window.google.maps) {
          clearInterval(interval);

          let addressField = document.getElementById("addressField");

          this.addressField = new window.google.maps.places.Autocomplete(
            addressField
          );

          if (this.userDetails?.preferred_branch?.country_code) {
            this.addressField.setComponentRestrictions({
              country: [this.userDetails.preferred_branch.country_code],
            });
          } else {
            this.addressField.setComponentRestrictions({
              country: ["AE"],
            });
          }

          addressField.setAttribute("placeholder", "");

          this.addressField.addListener("place_changed", () => {
            let place = this.addressField.getPlace();

            this.driverDetails = {
              ...this.driverDetails,
              coordinates: {
                latitude: place.geometry.location.lat(),
                longitude: place.geometry.location.lng(),
              },
              address: place.formatted_address,
            };
          });
        } else {
          loadGoogleInstance();
        }
      }, 100);
    },
    getProjectSelectList(id) {
      if (id) {
        this.$api.project
          .getProjectSelectList({ limit: "all", branch: id })
          .then((res) => {
            this.projectList = res.data;
            let defaultProject = this.projectList.find((v) => v.is_default);
            if (
              this.driverDetails &&
              this.driverDetails.project &&
              this.editMode
            ) {
              this.driverDetails.project = this.driverDetails.project;
            } else {
              this.driverDetails.project = defaultProject?.id;
            }
          })
          .catch((err) => {
            console.log(err);
          });
      } else {
        this.projectList = [];
      }
    },
    getDriverOptions() {
      bus.$emit("showLoader", true);
      this.$api.driver
        .getDriverOptions({ request_type: "form" })
        .then((res) => {
          this.customOptionFieldList = res?.data?.custom_fields;
          this.customObject = this.customFieldDefaultData(
            this.customOptionFieldList
          );
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
  },
  async mounted() {
    this.getDriverOptions();
    this.loadAutoComplete();
    await this.getBranchList();
    if (this.selectedBranch && !this.editMode) {
      this.driverDetails.branch = this.selectedBranch;
    }
    if (this.selectedBranch) {
      this.getVehicleList(this.selectedBranch);
      this.getProjectSelectList(this.selectedBranch);
      this.getZoneList(this.selectedBranch);
    }
    this.getFieldConfigurationList();
    if (this.$route && this.$route.params && this.$route.params.id) {
      this.getDriverObject(this.$route.params.id);
    }
  },
};
</script>

<style>
.v-select__slot label {
  z-index: 2;
}
.Map-Height {
  height: calc(100vh - 300px);
}
</style>
